import {
  apiUrl,
  TENANT_ID,
  BUSINESS_UNIT_ID,
} from "@/config/api";
import { stripHtml } from "@/lib/servicesApi";

export type ApiOfferBenefit = {
  name_en?: string;
  name_ar?: string;
  icon?: string;
};

export type ApiOffer = {
  uuid?: string;
  title?: string;
  subtitle?: string;
  desktop_image?: string;
  mobile_image?: string;
  description?: string | null;
  term_and_condition?: string | null;
  benefits?: ApiOfferBenefit[];
  status?: number;
  date_from?: string;
  date_to?: string;
  station_type?: string;
  coupon_enabled?: number;
  slug: string;
};

export type ActiveExpiredOffersResult = {
  available?: ApiOffer[];
  expired?: ApiOffer[];
};

export type ActiveExpiredOffersResponse = {
  success?: boolean;
  message?: string;
  result?: ActiveExpiredOffersResult;
};

const PLACEHOLDER_IMAGE = "/images/offer-placeholder.png";

export type ListedOffer = {
  id: string;
  name: string;
  description: string;
  image: string;
  category: string;
  subtitle: string;
  dateFrom: string;
  dateTo: string;
  isExpired: boolean;
  couponEnabled: boolean;
  orderIndex: number;
  slug: string;
};

export function pickOfferImage(o: ApiOffer): string {
  const d = o.desktop_image?.trim();
  const m = o.mobile_image?.trim();
  return d || m || PLACEHOLDER_IMAGE;
}

export function mapApiOfferToListed(
  o: ApiOffer,
  opts: { isExpired: boolean; orderIndex: number }
): ListedOffer | null {
  const id = o.uuid?.trim();
  if (!id) return null;
  const station = o.station_type?.trim() || "Offers";
  const desc = stripHtml(o.description ?? "").trim();
  const subtitle = o.subtitle?.trim() ?? "";
  return {
    id,
    name: o.title?.trim() || "Offer",
    description: desc.slice(0, 220) || subtitle,
    image: pickOfferImage(o),
    category: station,
    subtitle,
    dateFrom: o.date_from ?? "",
    dateTo: o.date_to ?? "",
    isExpired: opts.isExpired,
    couponEnabled: o.coupon_enabled === 1,
    orderIndex: opts.orderIndex,
    slug: o?.slug?.trim() || "",
  };
}

/** Maps CMS `available` offers only; expired offers are never listed in the app. */
export function mergeActiveAndExpiredToListed(
  result: ActiveExpiredOffersResult | undefined
): ListedOffer[] {
  if (!result) return [];
  let order = 0;
  const out: ListedOffer[] = [];
  for (const o of result.available ?? []) {
    const row = mapApiOfferToListed(o, {
      isExpired: false,
      orderIndex: order++,
    });
    if (row) out.push(row);
  }
  return out;
}

export async function fetchActiveAndExpiredOffers(
  langCode: "en" | "ar",
  dealOnly?: boolean
): Promise<ActiveExpiredOffersResponse | null> {
  const q = new URLSearchParams({ langCode });
  if (dealOnly) {
    q.set("deal_of_the_day", "1");
  }
  const res = await fetch(
    apiUrl(`/offers/active-and-expired-offers?${q.toString()}`),
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-tenant-id": TENANT_ID,
        "x-business-unit-id": BUSINESS_UNIT_ID,
      },
      cache: "no-store",
    }
  );
  if (!res.ok) return null;
  return (await res.json()) as ActiveExpiredOffersResponse;
}
